export const AUTH_COOKIE_NAME = process.env.NEXT_PUBLIC_AUTH_COOKIE_NAME || 'flipbook_token';

export const DEFAULT_AUTHENTICATED_PATH = '/dashboard';

/** Routes that never require authentication */
export const PUBLIC_ROUTES = [
  '/otp-verification',
  '/library',
  '/flipbook-viewer',
  '/read',
  '/q',
  '/force-login',
] as const;

/** Auth screens for signed-out users only */
export const GUEST_ONLY_ROUTES = ['/', '/forgot-password'] as const;

/** null = any authenticated user; string[] = any one of these permissions */
export type RoutePermission = string | string[] | null;
export type RouteRequirement = RoutePermission | { roles: string[] };

export const APPROVAL_PERMISSION_SLUGS = [
  'publish_approve_l1',
  'publish_approve_l2',
  'approve_book_l1',
  'approve_book_l2',
  'delete_approve_l1',
  'delete_approve_l2',
] as const;

/**
 * Menu/route level module permissions.
 * null = any authenticated user may access.
 */
export const ROUTE_PERMISSIONS: Record<string, RouteRequirement> = {
  '/dashboard': 'dashboard',
  '/admin-book-management': 'flipbook_library',
  '/access-history': 'access_history',
  '/books-approval': [...APPROVAL_PERMISSION_SLUGS],
  '/book-upload': 'upload_book',
  '/organizations': { roles: ['super_admin'] },
  '/user-management': 'user_management',
  '/access-logs': { roles: ['super_admin'] },
  '/masters': 'masters',
  '/settings': null,
  '/analytics': 'dashboard',
};

export const MENU_PERMISSIONS = {
  dashboard: 'dashboard',
  flipbook_library: 'flipbook_library',
  access_history: 'access_history',
  books_approval: [...APPROVAL_PERMISSION_SLUGS],
  upload_book: 'upload_book',
  user_management: 'user_management',
  access_logs: 'access_logs',
  masters: 'masters',
  settings: null,
} as const;

export function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

export function isPublicRoute(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return PUBLIC_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
}

export function isGuestOnlyRoute(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return GUEST_ONLY_ROUTES.some((route) => path === route);
}

export function isProtectedRoute(pathname: string): boolean {
  return !isPublicRoute(pathname) && !isGuestOnlyRoute(pathname);
}

export function getRequiredPermission(pathname: string): RouteRequirement | undefined {
  const path = normalizePathname(pathname);

  if (ROUTE_PERMISSIONS[path] !== undefined) {
    return ROUTE_PERMISSIONS[path];
  }

  const match = Object.keys(ROUTE_PERMISSIONS)
    .filter((route) => path.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];

  if (match) return ROUTE_PERMISSIONS[match];
  return undefined;
}

export type PermissionLike = {
  id?: number;
  slug: string;
};

export function getUserPermissionSlugs(
  user?: {
    permissions?: PermissionLike[];
    permission_ids?: number[];
  } | null
): string[] {
  if (!user) return [];
  if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    return user.permissions.map((p) => p.slug).filter(Boolean);
  }
  return [];
}

export function hasPermission(
  user: { permissions?: PermissionLike[] } | null | undefined,
  permissionSlug: RoutePermission | undefined
): boolean {
  if (permissionSlug == null) return true;
  if (!user) return false;
  const slugs = getUserPermissionSlugs(user);
  if (Array.isArray(permissionSlug)) {
    return permissionSlug.some((slug) => slugs.includes(slug));
  }
  return slugs.includes(permissionSlug);
}

export function canAccessPath(
  user:
    | {
        permissions?: PermissionLike[];
        role?: { slug?: string } | null;
      }
    | null
    | undefined,
  pathname: string
): boolean {
  const pathOnly = String(pathname || '').split('?')[0];
  const required = getRequiredPermission(pathOnly);
  // Unknown protected admin paths: deny by default for safety
  if (required === undefined && isProtectedRoute(pathOnly)) {
    return false;
  }
  if (required && typeof required === 'object' && !Array.isArray(required)) {
    return Boolean(user?.role?.slug && required.roles.includes(user.role.slug));
  }
  return hasPermission(user, required ?? null);
}

export function getFirstAllowedPath(
  user: { permissions?: PermissionLike[] } | null | undefined,
  fallback = DEFAULT_AUTHENTICATED_PATH
): string {
  const preferred = [
    '/dashboard',
    '/admin-book-management',
    '/access-history',
    '/books-approval',
    '/publish-books',
    '/book-upload',
    '/user-management',
    '/access-logs',
    '/masters/subjects',
    '/settings',
  ];

  for (const path of preferred) {
    if (canAccessPath(user, path)) return path;
  }

  return fallback;
}

/** Prevent open redirects — only allow same-origin relative paths */
export function getSafeNextPath(
  next: string | null | undefined,
  fallback = DEFAULT_AUTHENTICATED_PATH
): string {
  if (!next) return fallback;
  if (!next.startsWith('/') || next.startsWith('//') || next.includes('\\')) {
    return fallback;
  }
  if (isGuestOnlyRoute(next)) return fallback;
  return next;
}
